import { ChevronDownIcon } from "@heroicons/react/outline";
import { useState } from "react";

import { cn } from "@/lib/utils";

export interface SectionGroupProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  variant?: "default" | "compact" | "highlighted";
  icon?: React.ReactNode;
  headerAction?: React.ReactNode;
}

const SectionGroup = ({
  title,
  description,
  children,
  className,
  collapsible = false,
  defaultExpanded = true,
  variant = "default",
  icon,
  headerAction,
}: SectionGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const variantStyles = {
    default: "border border-gray-200",
    compact: "border border-gray-100",
    highlighted: "border border-blue-200/50",
  };

  const content = (
    <div
      className={cn("rounded-lg transition-all duration-200", variantStyles[variant], className)}
    >
      {title && (
        <div
          className={cn(
            "flex items-center justify-between",
            collapsible ? "cursor-pointer select-none px-4 py-3 hover:bg-gray-50/30" : "px-4 py-3",
            variant === "highlighted" && "border-b border-blue-200/30",
            variant === "default" && "border-b border-gray-200/50",
            variant === "compact" && description && "border-b border-gray-100",
          )}
          onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
        >
          <div className="flex items-center gap-2">
            {icon && (
              <div className="flex h-5 w-5 items-center justify-center text-gray-500">{icon}</div>
            )}
            <div>
              <h3
                className={cn(
                  "font-medium",
                  variant === "highlighted" ? "text-blue-900" : "text-gray-900",
                  description ? "text-sm" : "text-sm",
                )}
              >
                {title}
              </h3>
              {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {headerAction}
            {collapsible && (
              <ChevronDownIcon
                className={cn(
                  "h-4 w-4 text-gray-400 transition-transform duration-200",
                  isExpanded && "rotate-180",
                )}
              />
            )}
          </div>
        </div>
      )}

      {(!collapsible || isExpanded) && (
        <div
          className={cn(
            "space-y-4",
            title ? "p-4" : "p-3",
            variant === "compact" && "space-y-3 p-3",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );

  return content;
};

export default SectionGroup;
